import { Button, Spinner } from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import DataTable from "@/components/table/DataTable";
import { listProcesses } from "@/features/processes/api";
import { errorMessage } from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { showError, showSuccess } from "@/lib/toast";
import {
  createQuotationProcess,
  deleteQuotationProcess,
  listQuotationProcesses,
  updateQuotationProcess,
  type QuotationProcess,
  type QuotationProcessInput,
} from "./api";
import {
  buildQuotationProcessColumns,
  type ProcessBreakdownRow,
} from "./itemProcessColumns";

/**
 * 単価 × 数量。
 *
 * 円未満の桁で誤差が出ないよう、銭の整数にしてから掛ける。金額を 2 進小数の
 * まま掛けると 1 円のずれが出ることがある（API 側が NUMERIC で持っているのと
 * 同じ理由）。
 */
function multiply(unitPrice: string, quantity: number): string {
  const sen = Math.round(Number(unitPrice) * 100) * quantity;
  return (sen / 100).toFixed(2);
}

/** 何か入力されたか（空の値しか入っていない更新は保存しない）。 */
function hasValue(input: QuotationProcessInput): boolean {
  return Object.values(input).some((v) => v != null && v !== "");
}

/**
 * 明細の見積画面の右半分。工程ごとの金額を積んで、明細の単価を作る。
 *
 * 図面の明細には単価を直接持たせていない。ここで積んだ合算がそのまま単価になり、
 * 明細の金額は 合算 × 数量 になる（工程を直したのに単価が古いままという
 * 二重管理を避けるため）。
 *
 * **行は工程マスタが決める。** 使う工程を選んで足していくのではなく、登録されて
 * いる工程を**全部 0 円で並べ**、使う工程にだけ金額（または時間）を入れる。
 * 見積のたびに工程を選び直す手間が無くなり、入れ忘れた工程も空欄として目に入る。
 *
 * **0 円のままの工程は保存しない。** 金額や時間を入れた時点でその工程の内訳を
 * 起こす（`stored` が null の行＝まだ保存されていない工程）。使わない工程まで
 * 0 円の行として残すと、見積ごとに工程の数だけ行が増え、「この見積で実際に
 * 積んだ工程」が後から読めなくなるため。
 *
 * 時間単価を持つ工程に時間を入れると、マスタの単価を写して 時間 × 単価 を金額に
 * する。持たない工程（外注費など）は金額を直接入れる。どちらもあとから切り替え
 * られる。
 */
export default function ItemProcessPanel({
  quotationId,
  itemId,
  quantity,
  canWrite,
}: {
  quotationId: number;
  itemId: number;
  quantity: number;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [clearing, setClearing] = useState<ProcessBreakdownRow | null>(null);

  const rows = useQuery({
    queryKey: ["quotation-processes", quotationId, itemId],
    queryFn: () => listQuotationProcesses(quotationId, itemId),
  });
  // 行の並びは工程マスタそのもの（使用停止の工程は新しくは出さない）。
  const master = useQuery({
    queryKey: ["processes", "breakdown"],
    queryFn: () => listProcesses({ disabled: false, limit: 500, offset: 0 }),
  });

  const unitPrice = rows.data?.total_price ?? "0";

  /**
   * 工程マスタと保存済みの内訳を突き合わせて、表に出す行を作る。
   *
   * マスタの順（工程コード順）に 1 行ずつ並べ、その工程の内訳があれば中身を
   * 添える。**同じ工程の内訳が複数ある見積**（以前は同じ工程を 2 行積めた）は
   * そのまま全部出す。使用停止になった工程の内訳は、マスタに出てこないので
   * 最後に続ける（過去の見積を欠けたまま見せないため）。
   */
  const breakdown = useMemo<ProcessBreakdownRow[]>(() => {
    const stored = rows.data?.items ?? [];
    const byProcess = new Map<number, QuotationProcess[]>();
    for (const row of stored) {
      byProcess.set(row.process_id, [...(byProcess.get(row.process_id) ?? []), row]);
    }

    const built: Omit<ProcessBreakdownRow, "sequence">[] = [];
    for (const process of master.data?.items ?? []) {
      const existing = byProcess.get(process.process_id) ?? [];
      byProcess.delete(process.process_id);
      const base = {
        processId: process.process_id,
        processCode: process.process_code,
        processName: process.process_name,
        processDisabled: process.disabled,
      };
      if (existing.length === 0) {
        built.push({ ...base, key: `p${process.process_id}`, stored: null });
        continue;
      }
      for (const row of existing) {
        built.push({ ...base, key: `r${row.quotation_process_id}`, stored: row });
      }
    }
    // マスタから外れた工程（使用停止）の内訳。行の中身は内訳が持っている。
    for (const rest of byProcess.values()) {
      for (const row of rest) {
        built.push({
          key: `r${row.quotation_process_id}`,
          processId: row.process_id,
          processCode: row.process_code,
          processName: row.process_name,
          processDisabled: true,
          stored: row,
        });
      }
    }
    return built.map((row, index) => ({ ...row, sequence: index + 1 }));
  }, [rows.data, master.data]);

  /** 明細の単価と金額が変わるので、明細一覧とヘッダも引き直す。 */
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["quotation-processes", quotationId, itemId],
      }),
      queryClient.invalidateQueries({ queryKey: ["quotation-items", quotationId] }),
      queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] }),
      queryClient.invalidateQueries({ queryKey: ["quotations"] }),
    ]);
  };

  const clear = useMutation({
    mutationFn: (row: ProcessBreakdownRow) =>
      deleteQuotationProcess(quotationId, itemId, row.stored!.quotation_process_id),
    onSuccess: (_, row) => {
      showSuccess(`「${row.processName}」の入力を取り消しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setClearing(null),
  });

  /**
   * セルの 1 項目を保存する。
   *
   * **まだ内訳の無い工程ではその場で行を起こす**（0 円の行を先に作っておかない
   * のはこのため）。何も入っていない値だけを送られたときは起こさない。空欄の
   * まま Enter を押しただけで、使わない工程の行が増えてしまう。
   */
  const saveField = async (
    row: ProcessBreakdownRow,
    input: QuotationProcessInput,
  ): Promise<string | null> => {
    try {
      if (row.stored) {
        await updateQuotationProcess(
          quotationId,
          itemId,
          row.stored.quotation_process_id,
          input,
        );
      } else {
        if (!hasValue(input)) return null;
        // 金額は既定で 0。時間を入れた行では API 側が 時間 × 単価 で入れ直す。
        await createQuotationProcess(quotationId, itemId, {
          process_id: row.processId,
          price: "0",
          ...input,
        });
      }
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const columns = useMemo(
    () =>
      buildQuotationProcessColumns({
        canWrite,
        onSaveField: saveField,
        onClear: setClearing,
      }),
    // saveField は同一性が保たれないため依存に入れない（中身は id にしか依存しない）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite],
  );

  const table = useReactTable<ProcessBreakdownRow>({
    data: breakdown,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.key,
    columnResizeMode: "onChange",
    // 並び順は工程マスタそのもの。列ヘッダでの並び替えは持たない。
    enableSorting: false,
    // 行番号の列は 50px で足りる（既定の下限 60 だと合計が収まらない）。
    defaultColumn: { minSize: 50, maxSize: 600 },
  });

  const isPending = rows.isPending || master.isPending;
  const error = (rows.error ?? master.error) as Error | null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-end justify-between gap-2 p-3">
        <p className="max-w-sm text-sm text-default-500">
          工程マスタの工程をすべて並べています。使う工程にだけ金額（時間で見積る工程は時間）を入れてください。
        </p>

        <div className="ml-auto text-right text-sm">
          <div className="text-default-500">
            単価（合算）{" "}
            <span className="text-base font-bold text-default-700">
              {formatAmount(unitPrice)} 円
            </span>
          </div>
          <div className="text-default-500">
            金額（× {quantity}）{" "}
            <span className="font-bold text-default-700">
              {formatAmount(multiply(unitPrice, quantity))} 円
            </span>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        {error ? (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <p className="text-default-600">
              工程を取得できませんでした。
              <br />
              {error.message}
            </p>
            <Button
              color="primary"
              onPress={() => {
                void rows.refetch();
                void master.refetch();
              }}
            >
              再読み込み
            </Button>
          </div>
        ) : isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="読み込み中..." />
          </div>
        ) : (
          <DataTable
            table={table}
            isLoading={rows.isFetching || master.isFetching}
            emptyMessage="工程マスタに使える工程がありません。マスタ管理で登録してください。"
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={clearing != null}
        title="入力の取り消し"
        message={`「${clearing?.processName}」に入れた内容を取り消し、0 円に戻します。\nこの明細の単価が ${formatAmount(clearing?.stored?.price ?? "0")} 円 下がります。`}
        confirmLabel="取り消す"
        isDanger
        isBusy={clear.isPending}
        onClose={() => setClearing(null)}
        onConfirm={() => {
          if (clearing?.stored) clear.mutate(clearing);
        }}
      />
    </div>
  );
}
